import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Check, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useConfirm } from '@/components/ui/confirm-provider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * CategoryManagerDialog
 *
 * Full-featured category management for e-commerce:
 *   • List existing categories (active + inactive)
 *   • Create new (auto-slug from name, editable)
 *   • Inline edit (rename, description, toggle active)
 *   • Delete (with confirm + product-count safety)
 *
 * Backend: /projects/:projectId/ecommerce/categories — auth via existing
 * requireProjectAccess (works for owner, admin, moderator).
 */

interface Category {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  imageUrl?: string;
  parentId?: string;
  order: number;
  isActive: boolean;
  productCount?: number;
}

interface CategoryManagerDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const basePathFor = (projectId: string) => `/projects/${projectId}/ecommerce`;

// ============================================================================
// Helpers
// ============================================================================

/** Derive a URL-safe slug from a display name (matches backend regex). */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
}

// ============================================================================
// Component
// ============================================================================

export default function CategoryManagerDialog({
  projectId,
  open,
  onOpenChange,
}: CategoryManagerDialogProps) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const basePath = basePathFor(projectId);

  const {
    data: categoriesData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['ecommerce-categories', projectId],
    queryFn: async () => {
      const res = await api.get(`${basePath}/categories`);
      return res.data;
    },
    enabled: open && Boolean(projectId),
  });

  const categories: Category[] = (categoriesData?.data as Category[]) || [];

  // Row being edited (null = none)
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    // Reset local editing state whenever the dialog re-opens
    if (open) {
      setEditingId(null);
      setShowCreate(false);
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: async (input: Omit<Category, '_id' | 'order' | 'productCount'>) => {
      const res = await api.post(`${basePath}/categories`, input);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Category created');
      queryClient.invalidateQueries({ queryKey: ['ecommerce-categories', projectId] });
      setShowCreate(false);
      refetch();
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error?.code;
      if (code === 'DUPLICATE_SLUG') {
        toast.error('A category with that slug already exists');
      } else {
        toast.error(err?.response?.data?.error?.message || 'Create failed');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Category> }) => {
      const res = await api.put(`${basePath}/categories/${id}`, patch);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Category updated');
      queryClient.invalidateQueries({ queryKey: ['ecommerce-categories', projectId] });
      setEditingId(null);
      refetch();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Update failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`${basePath}/categories/${id}`);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Category deleted');
      queryClient.invalidateQueries({ queryKey: ['ecommerce-categories', projectId] });
      queryClient.invalidateQueries({ queryKey: ['products', projectId] });
      refetch();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Delete failed');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Manage categories</DialogTitle>
          <DialogDescription>
            Organize your products into categories. Slugs are used in URLs and must be unique per
            project.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* ── Toolbar ── */}
          <div className="flex items-center justify-between">
            <div className="text-muted-foreground text-sm">
              {isLoading
                ? 'Loading…'
                : `${categories.length} ${categories.length === 1 ? 'category' : 'categories'}`}
            </div>
            {!showCreate && (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                <Plus className="mr-1.5 h-4 w-4" />
                New category
              </Button>
            )}
          </div>

          {/* ── Create form (inline, appears at top when toggled) ── */}
          {showCreate && (
            <CategoryForm
              mode="create"
              initial={{ name: '', slug: '', description: '', isActive: true }}
              onCancel={() => setShowCreate(false)}
              onSubmit={(values) =>
                createMutation.mutate({
                  name: values.name,
                  slug: values.slug,
                  description: values.description,
                  isActive: values.isActive,
                } as any)
              }
              submitting={createMutation.isPending}
            />
          )}

          {/* ── List ── */}
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="text-primary h-5 w-5 animate-spin" />
            </div>
          ) : categories.length === 0 && !showCreate ? (
            <div className="rounded-lg border border-dashed p-8 text-center">
              <p className="text-muted-foreground text-sm">
                No categories yet. Click <span className="font-medium">New category</span> to create
                your first one.
              </p>
            </div>
          ) : (
            <ul className="divide-y rounded-lg border">
              {categories.map((cat) => (
                <li key={cat._id} className="p-3">
                  {editingId === cat._id ? (
                    <CategoryForm
                      mode="edit"
                      initial={cat}
                      onCancel={() => setEditingId(null)}
                      onSubmit={(values) =>
                        updateMutation.mutate({
                          id: cat._id,
                          patch: {
                            name: values.name,
                            slug: values.slug,
                            description: values.description,
                            isActive: values.isActive,
                          },
                        })
                      }
                      submitting={updateMutation.isPending}
                    />
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{cat.name}</span>
                          {!cat.isActive && (
                            <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="text-muted-foreground mt-0.5 font-mono text-xs">
                          /{cat.slug}
                        </div>
                        {cat.description && (
                          <p className="text-muted-foreground mt-1 line-clamp-2 text-sm">
                            {cat.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingId(cat._id)}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            const productCount = cat.productCount || 0;
                            const extra =
                              productCount > 0
                                ? `\n\n${productCount} product(s) currently use this category — they will be left uncategorized.`
                                : '';
                            if (
                              await confirm({
                                title: 'Delete category?',
                                message: `Delete "${cat.name}"?${extra}`,
                                danger: true,
                              })
                            ) {
                              deleteMutation.mutate(cat._id);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Shared category form (create + edit)
// ============================================================================

interface CategoryFormValues {
  name: string;
  slug: string;
  description: string;
  isActive: boolean;
}

interface CategoryFormProps {
  mode: 'create' | 'edit';
  initial: Partial<CategoryFormValues>;
  onCancel: () => void;
  onSubmit: (values: CategoryFormValues) => void;
  submitting: boolean;
}

function CategoryForm({ mode, initial, onCancel, onSubmit, submitting }: CategoryFormProps) {
  const [name, setName] = useState(initial.name || '');
  const [slug, setSlug] = useState(initial.slug || '');
  const [slugEdited, setSlugEdited] = useState(mode === 'edit');
  const [description, setDescription] = useState(initial.description || '');
  const [isActive, setIsActive] = useState(initial.isActive ?? true);

  // Auto-derive slug from name until user edits it
  useEffect(() => {
    if (!slugEdited) {
      setSlug(slugify(name));
    }
  }, [name, slugEdited]);

  const canSubmit =
    name.trim().length > 0 && slug.trim().length > 0 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit({
          name: name.trim(),
          slug: slug.trim(),
          description: description.trim(),
          isActive,
        });
      }}
      className="bg-muted/30 space-y-3 rounded-lg border p-3"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="cat-name">
            Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="cat-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Home Decor"
            autoFocus
            maxLength={100}
          />
        </div>
        <div>
          <Label htmlFor="cat-slug">
            Slug <span className="text-red-500">*</span>
          </Label>
          <Input
            id="cat-slug"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value.toLowerCase());
              setSlugEdited(true);
            }}
            placeholder="home-decor"
            pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
            maxLength={100}
            className="font-mono text-sm"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="cat-description">Description</Label>
        <Textarea
          id="cat-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional — shown on category landing pages"
          rows={2}
          maxLength={500}
        />
      </div>

      <div className="flex items-center gap-2">
        <Switch id="cat-active" checked={isActive} onCheckedChange={setIsActive} />
        <Label htmlFor="cat-active" className="cursor-pointer text-sm">
          Active (visible to shoppers)
        </Label>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
          <X className="mr-1.5 h-3.5 w-3.5" />
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!canSubmit || submitting}>
          {submitting ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="mr-1.5 h-3.5 w-3.5" />
          )}
          {mode === 'create' ? 'Create' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}
